import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, type TaxOfficeDictionary, type TaxOfficePreview } from '../api';
import { date, type Dictionary, type Locale } from '../i18n';
import * as Icon from '@firmeto/design/Icons';

/**
 * Słownik urzędów skarbowych (E11-S7, decyzja 4 z docs/04) — człowiek w pętli.
 *
 * Operator wgrywa plik ze schemy MF, widzi różnicę wobec bazy (dodane, usunięte, zmienione
 * nazwy) i zatwierdza dokładnie to, co obejrzał. Zastosować da się tylko wersję, którą importuje
 * generowana przez nas schema JPK — inną można obejrzeć, bo różnica mówi, co MF zmieniło.
 */

function failureText(failure: unknown): string {
  return failure instanceof ApiError ? failure.message : String(failure);
}

/** Nazwa z bazy jest wielkimi literami; na ekranie ładniej po polsku. */
function pretty(name: string): string {
  return name
    .toLowerCase()
    .replace(/(^|[\s(-])(\p{L})/gu, (_, before: string, letter: string) => before + letter.toUpperCase())
    .replace(/\b(W|We|Im|I)\b/g, (word) => word.toLowerCase());
}

export function TaxOffices({ t, locale }: { t: Dictionary; locale: Locale }) {
  const [dictionary, setDictionary] = useState<TaxOfficeDictionary | null>(null);
  const [preview, setPreview] = useState<TaxOfficePreview | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDictionary(await api.taxOffices());
    } catch (failure) {
      setError(failureText(failure));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function showDiff() {
    if (!file) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setPreview(await api.previewTaxOffices(file));
    } catch (failure) {
      setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      await api.applyTaxOffices(preview.import.id);
      setPreview(null);
      setFile(null);
      setNotice(t.taxOffices.applied);
      await load();
    } catch (failure) {
      setError(failureText(failure));
    } finally {
      setBusy(false);
    }
  }

  const filtered = useMemo(() => {
    if (!dictionary) return [];
    const q = query.trim().toLowerCase();
    if (!q) return dictionary.offices;
    return dictionary.offices.filter((office) => office.code.startsWith(q) || office.name.toLowerCase().includes(q));
  }, [dictionary, query]);

  const diffEmpty = preview !== null && preview.diff.added.length + preview.diff.removed.length + preview.diff.changed.length === 0;

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.taxOffices.title}</h3>
          <p>{t.taxOffices.lead}</p>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {notice && !error && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{notice}</b>
          </div>
        </div>
      )}

      {dictionary === null && !error && <p className="muted">{t.loading}</p>}

      {dictionary && (
        <div className="grid two">
          <div className="panel">
            <div className="hd">
              <div>
                <h4>{t.taxOffices.list}</h4>
              </div>
            </div>
            <p>
              {t.taxOffices.pinned}: <b className="mono">{dictionary.pinnedVersion}</b> · {t.taxOffices.inDb}:{' '}
              <b className="mono">{dictionary.versionsInDb.join(', ') || '—'}</b>{' '}
              {dictionary.consistent ? <span className="chip st-ok">{t.taxOffices.consistent}</span> : <span className="chip st-bad">{t.taxOffices.inconsistent}</span>}
            </p>
            <p className="hint">
              {t.taxOffices.active}: {dictionary.active} · {t.taxOffices.specialised}: {dictionary.specialised} · {t.taxOffices.inactive}: {dictionary.inactive}
            </p>
          </div>

          <div className="panel">
            <div className="hd">
              <div>
                <h4>{t.taxOffices.upload}</h4>
                <p>{t.taxOffices.uploadHint}</p>
              </div>
            </div>
            <div className="row">
              <label className="fld" style={{ flex: 2, minWidth: 260 }}>
                <span>{t.taxOffices.file}</span>
                <input
                  className="inp"
                  type="file"
                  accept=".xsd,application/xml,text/xml"
                  onChange={(event) => {
                    setFile(event.target.files?.[0] ?? null);
                    setPreview(null);
                  }}
                />
              </label>
              <div className="fld">
                <span>&nbsp;</span>
                <button type="button" className="btn" onClick={() => void showDiff()} disabled={busy || !file}>
                  {t.taxOffices.preview}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {preview && dictionary && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.taxOffices.previewTitle}</h4>
              <p>
                {t.taxOffices.file}: <span className="mono">{preview.import.fileName}</span> · {t.taxOffices.version}:{' '}
                <span className="mono">{preview.import.sourceVersion}</span> · {t.taxOffices.added}: {preview.import.added} · {t.taxOffices.removed}:{' '}
                {preview.import.removed} · {t.taxOffices.changed}: {preview.import.changed} · {t.taxOffices.unchanged}: {preview.import.unchanged}
              </p>
            </div>
            <div className="sp">
              <button type="button" className="btn ghost" onClick={() => setPreview(null)} disabled={busy}>
                {t.taxOffices.cancel}
              </button>
              <button type="button" className="btn" onClick={() => void apply()} disabled={busy || !preview.import.matchesPin || diffEmpty}>
                {t.taxOffices.apply}
              </button>
            </div>
          </div>

          {!preview.import.matchesPin && (
            <div className="alert a-warn">
              <Icon.Warn />
              <div>{t.taxOffices.versionMismatch(preview.import.sourceVersion, dictionary.pinnedVersion)}</div>
            </div>
          )}
          {diffEmpty && <p className="muted">{t.taxOffices.noChanges}</p>}

          {preview.diff.changed.length > 0 && (
            <>
              <h5>{t.taxOffices.changed}</h5>
              <div className="tbl">
                <table>
                  <thead>
                    <tr>
                      <th>{t.taxOffices.code}</th>
                      <th className="wrap">{t.taxOffices.before}</th>
                      <th className="wrap">{t.taxOffices.after}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.diff.changed.map((change) => (
                      <tr key={change.code}>
                        <td className="mono">{change.code}</td>
                        <td className="wrap">
                          {change.before}
                          {change.specialisedBefore && <span className="chip st-warn">{t.taxOffices.specialisedFlag}</span>}
                        </td>
                        <td className="wrap">
                          <b>{change.after}</b>
                          {change.specialisedAfter && <span className="chip st-warn">{t.taxOffices.specialisedFlag}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {preview.diff.added.length > 0 && (
            <>
              <h5>{t.taxOffices.added}</h5>
              <div className="tbl">
                <table>
                  <tbody>
                    {preview.diff.added.map((entry) => (
                      <tr key={entry.code}>
                        <td className="mono">{entry.code}</td>
                        <td className="wrap">
                          {entry.name}
                          {entry.specialised && <span className="chip st-warn">{t.taxOffices.specialisedFlag}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {preview.diff.removed.length > 0 && (
            <>
              <h5>{t.taxOffices.removed}</h5>
              <div className="tbl">
                <table>
                  <tbody>
                    {preview.diff.removed.map((row) => (
                      <tr key={row.code}>
                        <td className="mono">{row.code}</td>
                        <td className="wrap">{row.name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {dictionary && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.taxOffices.history}</h4>
            </div>
          </div>
          {dictionary.imports.length === 0 ? (
            <p className="muted">{t.taxOffices.noHistory}</p>
          ) : (
            <div className="tbl">
              <table>
                <thead>
                  <tr>
                    <th>{t.taxOffices.uploadedAt}</th>
                    <th className="wrap">{t.taxOffices.file}</th>
                    <th>{t.taxOffices.version}</th>
                    <th className="n">{t.taxOffices.added}</th>
                    <th className="n">{t.taxOffices.removed}</th>
                    <th className="n">{t.taxOffices.changed}</th>
                    <th>{t.offices.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {dictionary.imports.map((item) => (
                    <tr key={item.id}>
                      <td>{date(item.uploadedAt, locale)}</td>
                      <td className="wrap mono">{item.fileName}</td>
                      <td className="mono">
                        {item.sourceVersion}
                        {!item.matchesPin && <span className="chip st-warn">≠ {dictionary.pinnedVersion}</span>}
                      </td>
                      <td className="n mono">{item.added}</td>
                      <td className="n mono">{item.removed}</td>
                      <td className="n mono">{item.changed}</td>
                      <td>
                        <span className={`chip ${item.status === 'APPLIED' ? 'st-ok' : 'st-draft'}`}>{t.taxOffices.status[item.status]}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {dictionary && (
        <div className="panel">
          <div className="hd">
            <div>
              <h4>{t.taxOffices.list}</h4>
            </div>
          </div>
          <label className="fld">
            <span>{t.taxOffices.search}</span>
            <input className="inp" value={query} placeholder="1471" onChange={(event) => setQuery(event.target.value)} />
          </label>
          <p className="hint">{t.taxOffices.count(filtered.length)}</p>
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.taxOffices.code}</th>
                  <th className="wrap">{t.taxOffices.name}</th>
                  <th>{t.taxOffices.version}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 500).map((office) => (
                  <tr key={office.code}>
                    <td className="mono">{office.code}</td>
                    <td className="wrap">
                      {pretty(office.name)}
                      {office.specialised && <span className="chip st-warn">{t.taxOffices.specialisedFlag}</span>}
                      {!office.active && <span className="chip st-bad">{t.taxOffices.inactiveFlag}</span>}
                    </td>
                    <td className="mono">{office.sourceVersion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
